import React, { useEffect, useState } from 'react';
import { collection, getDocs, addDoc, query, orderBy, limit, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '@/config/firebase';
import toast from 'react-hot-toast';
import {
  Send,
  Bell,
  Mail,
  Users,
  Building2,
  RefreshCw,
  MessageSquare,
  Calendar,
  CheckCircle,
  AlertTriangle,
  Megaphone
} from 'lucide-react';

interface Business {
  id: string;
  name: string;
  adminEmail: string;
}

interface BroadcastMessage {
  id: string;
  title: string;
  message: string;
  type: 'announcement' | 'maintenance' | 'update';
  targetAudience: 'all' | 'admins' | 'users';
  createdAt: any;
  sentBy: string;
}

const SuperAdminCommunicationsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [history, setHistory] = useState<BroadcastMessage[]>([]);
  
  // Formulario
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'announcement' | 'maintenance' | 'update'>('announcement');
  const [targetAudience, setTargetAudience] = useState<'all' | 'admins' | 'users'>('admins');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Cargar negocios
      const businessSnap = await getDocs(collection(db, 'businesses'));
      const businessList = businessSnap.docs.map(d => ({
        id: d.id,
        name: d.data().name,
        adminEmail: d.data().adminEmail
      }));
      setBusinesses(businessList);

      // Cargar historial de mensajes
      try {
        const historyQuery = query(
          collection(db, 'broadcasts'),
          orderBy('createdAt', 'desc'),
          limit(10)
        );
        const historySnap = await getDocs(historyQuery);
        const historyList = historySnap.docs.map(d => ({
          id: d.id,
          ...d.data()
        })) as BroadcastMessage[];
        setHistory(historyList);
      } catch (e) {
        // Colección puede no existir
        setHistory([]);
      }
    } catch (e) {
      console.error('Error cargando datos:', e);
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSendBroadcast = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error('Completa el título y el mensaje');
      return;
    }

    if (!confirm(`¿Enviar este mensaje a ${targetAudience === 'all' ? 'todos los usuarios' : targetAudience === 'admins' ? 'todos los administradores' : 'todos los usuarios'}?`)) {
      return;
    }

    setSending(true);
    try {
      // Crear notificaciones para cada negocio
      const batch: Promise<any>[] = [];

      // Primero obtener todos los usuarios de cada negocio según la audiencia
      for (const business of businesses) {
        // Obtener usuarios del negocio
        let usersToNotify: string[] = [];
        
        const usersQuery = query(
          collection(db, 'users'),
          where('businessId', '==', business.id)
        );
        const usersSnap = await getDocs(usersQuery);
        
        usersSnap.docs.forEach(userDoc => {
          const userData = userDoc.data();
          // Filtrar según audiencia
          if (targetAudience === 'all') {
            usersToNotify.push(userDoc.id);
          } else if (targetAudience === 'admins' && (userData.role === 'admin' || userData.role === 'super_admin')) {
            usersToNotify.push(userDoc.id);
          } else if (targetAudience === 'users' && userData.role === 'user') {
            usersToNotify.push(userDoc.id);
          }
        });

        // Crear notificación para cada usuario
        for (const userId of usersToNotify) {
          const notificationData = {
            userId: userId,
            businessId: business.id,
            title: title,
            message: message,
            type: messageType === 'maintenance' ? 'warning' : messageType === 'update' ? 'info' : 'info',
            isRead: false,
            createdAt: new Date(),
            fromSuperAdmin: true
          };

          batch.push(
            addDoc(collection(db, 'notifications'), notificationData)
          );
        }
      }

      await Promise.all(batch);

      // Enviar notificaciones push a todos los usuarios
      try {
        console.log('📤 Preparando envío de notificaciones push...');
        const functions = getFunctions();
        const sendPushNotification = httpsCallable(functions, 'sendPushNotification');
        
        // Recopilar todos los userIds para enviar push
        const allUserIds: string[] = [];
        for (const business of businesses) {
          const usersQuery = query(
            collection(db, 'users'),
            where('businessId', '==', business.id)
          );
          const usersSnap = await getDocs(usersQuery);
          usersSnap.docs.forEach(userDoc => {
            const userData = userDoc.data();
            if (targetAudience === 'all') {
              allUserIds.push(userDoc.id);
            } else if (targetAudience === 'admins' && (userData.role === 'admin' || userData.role === 'super_admin')) {
              allUserIds.push(userDoc.id);
            } else if (targetAudience === 'users' && userData.role === 'user') {
              allUserIds.push(userDoc.id);
            }
          });
        }

        console.log(`📤 UserIds recopilados: ${allUserIds.length}`, allUserIds);

        if (allUserIds.length > 0) {
          console.log('📤 Llamando a sendPushNotification...');
          const result = await sendPushNotification({
            userIds: allUserIds,
            notification: {
              title: title,
              body: message,
              url: '/dashboard',
              data: {
                type: messageType,
                fromSuperAdmin: 'true'
              }
            }
          });
          console.log('📤 Resultado de sendPushNotification:', result.data);
          toast.success(`Push enviado: ${(result.data as any)?.successCount || 0} éxitos, ${(result.data as any)?.failureCount || 0} fallos`);
        } else {
          console.log('⚠️ No hay userIds para enviar push');
        }
      } catch (pushError: any) {
        console.error('❌ Error enviando push notifications:', pushError);
        toast.error(`Error en push: ${pushError.message || 'Error desconocido'}`);
      }

      // Guardar en historial
      await addDoc(collection(db, 'broadcasts'), {
        title,
        message,
        type: messageType,
        targetAudience,
        createdAt: new Date(),
        sentBy: 'Super Admin',
        businessCount: businesses.length
      });

      toast.success(`Mensaje enviado a ${businesses.length} negocios`);
      
      // Limpiar formulario
      setTitle('');
      setMessage('');
      
      // Recargar historial
      await loadData();
    } catch (e: any) {
      console.error('Error enviando mensaje:', e);
      toast.error('Error enviando el mensaje');
    } finally {
      setSending(false);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'maintenance':
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case 'update':
        return <CheckCircle className="w-4 h-4 text-blue-500" />;
      default:
        return <Megaphone className="w-4 h-4 text-green-500" />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'maintenance': return 'Mantenimiento';
      case 'update': return 'Actualización';
      default: return 'Anuncio';
    }
  };

  const getAudienceLabel = (audience: string) => {
    switch (audience) {
      case 'admins': return 'Administradores';
      case 'users': return 'Usuarios';
      default: return 'Todos';
    }
  };

  const formatDate = (date: any) => {
    if (!date) return 'N/A';
    const d = date?.toDate?.() || new Date(date);
    return d.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Megaphone className="w-7 h-7 text-amber-500" />
          Comunicación Global
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Envía notificaciones a todos los negocios de la plataforma
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Formulario de envío */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Send className="w-5 h-5 text-amber-500" />
              Nuevo Mensaje
            </h2>
          </div>
          <div className="p-4 space-y-4">
            {/* Tipo de mensaje */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tipo de mensaje
              </label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => setMessageType('announcement')}
                  className={`p-3 rounded-lg border text-sm flex flex-col items-center gap-1 transition-colors ${
                    messageType === 'announcement'
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <Megaphone className="w-5 h-5" />
                  Anuncio
                </button>
                <button
                  onClick={() => setMessageType('update')}
                  className={`p-3 rounded-lg border text-sm flex flex-col items-center gap-1 transition-colors ${
                    messageType === 'update'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <CheckCircle className="w-5 h-5" />
                  Actualización
                </button>
                <button
                  onClick={() => setMessageType('maintenance')}
                  className={`p-3 rounded-lg border text-sm flex flex-col items-center gap-1 transition-colors ${
                    messageType === 'maintenance'
                      ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <AlertTriangle className="w-5 h-5" />
                  Mantenimiento
                </button>
              </div>
            </div>

            {/* Audiencia */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Destinatarios
              </label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => setTargetAudience('admins')}
                  className={`p-3 rounded-lg border text-sm flex flex-col items-center gap-1 transition-colors ${
                    targetAudience === 'admins'
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <Building2 className="w-5 h-5" />
                  Admins
                </button>
                <button
                  onClick={() => setTargetAudience('users')}
                  className={`p-3 rounded-lg border text-sm flex flex-col items-center gap-1 transition-colors ${
                    targetAudience === 'users'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <Users className="w-5 h-5" />
                  Usuarios
                </button>
                <button
                  onClick={() => setTargetAudience('all')}
                  className={`p-3 rounded-lg border text-sm flex flex-col items-center gap-1 transition-colors ${
                    targetAudience === 'all'
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <Users className="w-5 h-5" />
                  Todos
                </button>
              </div>
            </div>

            {/* Título */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Título
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ej: Nueva funcionalidad disponible"
                className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400"
              />
            </div>

            {/* Mensaje */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Mensaje
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Escribe el contenido del mensaje..."
                rows={4}
                className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 resize-none"
              />
            </div>

            {/* Info de envío */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Este mensaje se enviará a <strong>{businesses.length} negocios</strong>
              </p>
            </div>

            {/* Botón de envío */}
            <button
              onClick={handleSendBroadcast}
              disabled={sending || !title.trim() || !message.trim()}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Enviar Mensaje
                </>
              )}
            </button>
          </div>
        </div>

        {/* Historial */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-500" />
              Historial
            </h2>
          </div>
          <div className="p-4">
            {history.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-20" />
                <p className="text-sm">No hay mensajes enviados</p>
              </div>
            ) : (
              <div className="space-y-3">
                {history.map((msg) => (
                  <div
                    key={msg.id}
                    className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                  >
                    <div className="flex items-start gap-2">
                      {getTypeIcon(msg.type)}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white text-sm truncate">
                          {msg.title}
                        </p>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {msg.message}
                        </p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                          <span className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-600 rounded">
                            {getTypeLabel(msg.type)}
                          </span>
                          <span>→ {getAudienceLabel(msg.targetAudience)}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          {formatDate(msg.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lista de negocios que recibirán el mensaje */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Building2 className="w-5 h-5 text-gray-500" />
            Negocios Destinatarios ({businesses.length})
          </h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {businesses.map((business) => (
              <div
                key={business.id}
                className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
              >
                <div className="w-8 h-8 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {business.name}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {business.adminEmail}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminCommunicationsPage;

